'use client';

import Image from 'next/image';
import { BiCheck, BiStar } from 'react-icons/bi';
import { HiOutlineCpuChip, HiOutlineTrophy } from 'react-icons/hi2';
import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import LouiseButton, { CtaTargetType } from '@/components/buttons/LouiseButton';
import ReactHtmlParser from 'react-html-parser';
import { useModal } from '@/components/ModalContext/ModalContext';

type Props = { locale: string };

const ChooseYourApproach = ({ locale }: Props) => {
  const { t } = useTranslation(locale);
  const { openModal } = useModal();

  const items = [
    {
      imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25020812324340_ai_chatbot-min.png',
      title: t('Home/ChooseYourApproach/key2'),
      description: t('Home/ChooseYourApproach/key2-2'),
      features: [
        t('Home/ChooseYourApproach/key2-3'),
        t('Home/ChooseYourApproach/key2-4'),
        t('Home/ChooseYourApproach/key2-5'),
      ],
      button: { title: t('BuildSmarter/watch-demo') },
      isPopular: false,
      includeAI: false,
      engaging: false,
      basedOnAI: true,
      ctaVariant: 'secondary',
      ctaTarget: 'discover-chatbot-ai',
    },
    {
      imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25020812324447_chatbot_with_scenario-min.png',
      title: t('Home/ChooseYourApproach/key1'),
      description: t('Home/ChooseYourApproach/key1-2'),
      features: [
        t('Home/ChooseYourApproach/key1-3'),
        t('Home/ChooseYourApproach/key1-4'),
        t('Home/ChooseYourApproach/key1-5'),
      ],
      button: { title: t('BuildSmarter/watch-demo') },
      isPopular: true,
      includeAI: true,
      engaging: false,
      basedOnAI: false,
      ctaVariant: 'primary',
      ctaTarget: 'discover-chatbot-text',
    },
    {
      imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25020812332876_video_pop_up-min.png',
      title: t('Home/ChooseYourApproach/key3'),
      description: t('Home/ChooseYourApproach/key3-2'),
      features: [
        t('Home/ChooseYourApproach/key3-3'),
        t('Home/ChooseYourApproach/key3-4'),
        t('Home/ChooseYourApproach/key3-5'),
      ],
      button: { title: t('BuildSmarter/watch-demo') },
      isPopular: false,
      includeAI: false,
      engaging: true,
      basedOnAI: false,
      ctaVariant: 'secondary',
      ctaTarget: 'discover-videobot',
    },
  ];

  const handleOpenDemoModal = (ctaTarget: string) => {
    switch (ctaTarget) {
      case 'discover-chatbot-text':
        openModal('DISCOVER_CHATBOT_TEXT', {});
        return;
      case 'discover-chatbot-ai':
        openModal('DISCOVER_CHATBOT_AI', {});
        return;
      case 'discover-videobot':
        openModal('DISCOVER_VIDEOBOT', {});
        return;
      default:
        return;
    }
  };

  return (
    <section className="tw-pt-16 tw-pb-2 lg:tw-pt-20 lg:tw-pb-20  tw-bg-transparent>">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend']">
            {t('Home/ChooseYourApproach/subtitleLight')}
          </h2>
        </div>

        {/* Cards Grid - Changed to 3 columns */}
        <div className="tw-grid grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          {items.map((item, index) => (
            <div
              key={index}
              className={`tw-relative tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-200 tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-flex tw-flex-col ${
                item.isPopular ? 'tw-ring-4 tw-ring-blue-600 tw-ring-opacity-80' : ''
              }`}
            >
              {/* Badges Container */}
              <div className="tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-flex tw-flex-col tw-gap-2">
                {/* Popular Badge */}
                {item.isPopular && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-600">
                    <BiStar className="tw-w-3 tw-h-3" />
                    {t('Home/ChooseYourApproach/badge-most-popular')}
                  </span>
                )}

                {/* Popular Badge */}
                {item.engaging && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-yellow-400 tw-text-gray-600">
                    <HiOutlineTrophy className="tw-w-3 tw-h-3" />
                    {t('Home/ChooseYourApproach/engaging')}
                  </span>
                )}

                {/* AI Powered Badge */}
                {item.basedOnAI && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-pink-100 tw-text-purple-800">
                    <HiOutlineCpuChip className="tw-w-3 tw-h-3" />
                    {t('Home/ChooseYourApproach/ai-powered')}
                  </span>
                )}

                {/* AI Powered Badge */}
                {item.includeAI && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-pink-100 tw-text-purple-800">
                    <HiOutlineCpuChip className="tw-w-3 tw-h-3" />
                    {t('Home/ChooseYourApproach/includeAI')}
                  </span>
                )}
              </div>

              {/* Image */}
              <div className="tw-relative tw-overflow-hidden tw-p-1">
                <div className="tw-rounded-t-2xl tw-overflow-hidden tw-ring-1 tw-ring-gray-200/50 tw-shadow-md">
                  <Image
                    priority
                    quality={100}
                    className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
                    src={item.imgSrc}
                    width={600}
                    height={400}
                    alt={`${item.title} - AI Service`}
                  />
                </div>
              </div>

              {/* Content - Now with flex-grow to push button to bottom */}
              <div className="tw-p-6 sm:tw-p-8 tw-flex tw-flex-col tw-flex-grow">
                <h3 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend']">
                  {item.title}
                </h3>

                <p className="tw-text-gray-600 tw-mb-6 tw-line-clamp-3 tw-font-['Lexend'] tw-text-sm">
                  {item.description}
                </p>

                {/* Features - Now with flex-grow */}
                <ul className="tw-space-y-3 tw-mb-8 tw-flex-grow">
                  {item.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="tw-flex tw-items-start tw-gap-3">
                      <BiCheck className="tw-w-5 tw-h-5 tw-text-green-500 tw-flex-shrink-0 tw-mt-0.5" />
                      <span className="tw-text-gray-700 tw-text-sm tw-font-['Lexend']">{ReactHtmlParser(feature)}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button - Now at bottom */}
                <div className="tw-mt-auto">
                  <LouiseButton
                    color="blue"
                    cta={item.button.title}
                    ctaTarget={item.ctaTarget as CtaTargetType}
                    gradiant={false}
                    locale={locale}
                    onClick={() => handleOpenDemoModal(item.ctaTarget)}
                    fullWith={true}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChooseYourApproach;
